import { useState } from 'react';
import { atlasEvals, type AtlasEvalsView } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { ErrorState, Skeleton } from '../../ui/states';
import { noDash, stop } from './common';
import { OwnModelPanel } from './OwnModelPanel';

const TAG = 'display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;';
const TAG_LIVE = TAG + 'color:#1F6B52;background:rgba(47,138,111,.13);';
const TAG_GREY = TAG + 'color:#5B6B7C;background:rgba(91,107,124,.13);';
const COLS = 'display:grid;grid-template-columns:minmax(112px,1fr) 40px 40px 52px;gap:7px;';
const pct = (v: number, d: number) => (v * 100).toFixed(d) + '%';

/**
 * Eval (design: atIsEval, V4 T14066): the live de-identification and routing scorecard
 * (GET /api/aithora/atlas/evals). Failures are shown, not smoothed. V4 words the header so it
 * cannot be read as our own model graduating (L35166): a practice test on fixed cases, not the
 * graduation exam. The owner's own-model progress panel sits under it (Spec V4 16 H).
 */
export function EvalTab() {
  const { client } = useSession();
  const [run, setRun] = useState(1);
  const q = useQuery<AtlasEvalsView>((signal) => atlasEvals(client, { signal }), [client, run]);
  if (!q.data) {
    return q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : <Skeleton rows={6} label="Running the eval" />;
  }
  const v = q.data;
  const e = v.evals;
  const d = e.deid;
  const r = e.routing;
  const cats = d.byCategory ?? null;
  const classes = d.identifierClasses ?? null;
  const results = r.results ?? r.misses;
  const leakQ = cats ? cats.reduce((n, c) => n + c.quarantined, 0) : Math.round(d.quarantineRate * d.cases);
  const tiles = [
    { label: 'Leak rate', fig: pct(d.leakRate, 2), note: 'gate ' + pct(e.thresholds.maxLeakRate, 0) + ' · ' + d.phiLeaked + ' / ' + d.phiTotal + ' items', color: d.phiLeaked ? '#B3261E' : '#1F6B52' },
    { label: 'Quarantined', fig: pct(d.quarantineRate, 2), note: leakQ ? leakQ + ' held fail-closed' : 'nothing withheld', color: leakQ ? '#5B6B7C' : '#1F6B52' },
    { label: 'Routing accuracy', fig: pct(r.accuracy, 1), note: 'gate ' + pct(e.thresholds.minRoutingAcc, 0) + ' · ' + r.correct + ' / ' + r.cases, color: r.accuracy >= e.thresholds.minRoutingAcc ? '#1F6B52' : '#B3261E' },
    { label: 'Corpus', fig: String(d.phiTotal + r.cases), note: d.phiTotal + ' PHI · ' + r.cases + ' routing', color: '#2F5C97' },
    { label: 'Coverage', fig: cats && classes ? cats.length + ' / ' + classes : '–', note: 'HIPAA identifier classes tested', color: '#5B6B7C' },
  ];
  const misses = r.misses.map((m) => '“' + m.q + '” routed to ' + m.got + ', expected ' + m.expected + '.');
  const notes = [
    ...e.notes.map(noDash),
    ...(cats && classes ? [cats.length + ' of ' + classes + ' HIPAA identifier classes are covered. The ' + (classes - cats.length) + ' untested classes are not claimed as clean.'] : []),
    ...(misses.length ? ['Routing miss this run: ' + misses[0]] : []),
  ];
  const cases = d.phiTotal + r.cases;
  const meta = ['run ' + run, cases + ' fixed cases' + (e.durationMs != null ? ' in ' + (e.durationMs / 1000).toFixed(1) + 's' : ''), 'this is not the graduation exam', 'graduation not started'].join(' · ');

  return (
    <>
      <div data-testid="atlas-eval" style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:13px;flex-wrap:wrap;row-gap:8px;')}>
        <span
          style={sx(
            'display:inline-flex;align-items:center;gap:9px;height:38px;padding:0 17px;border-radius:9px;font-size:16px;font-weight:700;letter-spacing:.04em;white-space:nowrap;' +
              (v.pass ? 'color:#1F6B52;background:rgba(47,138,111,.12);border:1px solid rgba(47,138,111,.34);' : 'color:#B3261E;background:rgba(179,38,30,.11);border:1px solid rgba(179,38,30,.34);'),
          )}
        >
          {v.pass ? 'PRACTICE TEST PASSED' : 'PRACTICE TEST FAILED'}
        </span>
        {v.provenance.provenance === 'LIVE' && <span style={sx(TAG_LIVE)}>LIVE</span>}
        <span style={sx(d.nerLive ? TAG_LIVE : TAG_GREY)}>{d.nerLive ? 'NER LIVE' : 'NER STUB'}</span>
        <span style={sx('font-size:16px;color:#54606C;')}>{meta}</span>
        <span style={sx('flex:1;')} />
        <a
          href="#"
          onClick={(ev) => {
            stop(ev);
            setRun((n) => n + 1);
          }}
          style={sx('display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 13px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;')}
        >
          Re-run
        </a>
      </div>

      <OwnModelPanel />

      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);margin-bottom:13px;')}>
        {tiles.map((t, i) => (
          <div key={t.label} style={sx('padding:12px 15px;box-sizing:border-box;' + (i < tiles.length - 1 ? 'border-right:1px solid rgba(34,39,46,.09);' : ''))}>
            <div style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;margin-bottom:6px;')}>{t.label}</div>
            <div style={sx('font-size:17px;font-weight:700;line-height:1.2;letter-spacing:-.015em;font-variant-numeric:tabular-nums;color:' + t.color + ';')}>{t.fig}</div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:5px;')}>{t.note}</div>
          </div>
        ))}
      </div>

      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;min-width:0;')}>
        <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:13px;align-items:start;')}>
          <div style={sx('box-sizing:border-box;flex:0 0 auto;border-top:1px solid rgba(34,39,46,.14);padding:0;min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;padding:11px 14px;border-bottom:1px solid rgba(34,39,46,.1);')}>
              <span style={sx('font-size:16px;font-weight:600;letter-spacing:-.01em;color:#22272E;')}>De-identification by category</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:14px;color:#54606C;')}>{d.phiLeaked + ' leaked of ' + d.phiTotal}</span>
            </div>
            <div style={sx(COLS + 'padding:8px 14px;border-bottom:1px solid rgba(34,39,46,.09);')}>
              {[['Category', ''], ['n', 'text-align:right;'], ['Leak', 'text-align:right;'], ['Quar.', 'text-align:right;']].map(([n, a]) => (
                <span key={n} style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;' + a)}>{n}</span>
              ))}
            </div>
            {(cats ?? []).map((c) => (
              <div key={c.category} style={sx(COLS + 'padding:8px 14px;align-items:center;border-bottom:1px solid rgba(34,39,46,.06);')}>
                <span style={sx('font-size:14px;color:#22272E;min-width:0;overflow-wrap:break-word;')}>{c.category}</span>
                <span style={sx('font-size:14px;font-weight:600;font-variant-numeric:tabular-nums;color:#54606C;text-align:right;')}>{String(c.n)}</span>
                <span style={sx('font-size:14px;font-variant-numeric:tabular-nums;font-weight:' + (c.leaked ? '700' : '600') + ';text-align:right;color:' + (c.leaked ? '#B3261E' : '#1F6B52') + ';')}>{String(c.leaked)}</span>
                <span style={sx('font-size:14px;font-variant-numeric:tabular-nums;font-weight:' + (c.quarantined ? '700' : '600') + ';text-align:right;color:' + (c.quarantined ? '#5B6B7C' : '#54606C') + ';')}>{String(c.quarantined)}</span>
              </div>
            ))}
            <div style={sx('font-size:14px;color:#54606C;padding:11px 14px;line-height:1.55;')}>
              Quarantine is not redaction. A quarantined item was withheld whole because the recognizer could not confirm it was clean, which is why the leak rate can be zero while coverage is incomplete.
            </div>
          </div>

          <div style={sx('box-sizing:border-box;flex:0 0 auto;border-top:1px solid rgba(34,39,46,.14);padding:0;min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;padding:11px 14px;border-bottom:1px solid rgba(34,39,46,.1);')}>
              <span style={sx('font-size:16px;font-weight:600;letter-spacing:-.01em;color:#22272E;')}>Routing by intent</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:14px;color:#54606C;')}>{r.correct + ' / ' + r.cases + ' correct'}</span>
            </div>
            {results.map((x) => {
              const ok = x.expected === x.got;
              return (
                <div key={x.q} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:8px 14px;border-bottom:1px solid rgba(34,39,46,.06);' + (ok ? '' : 'background:rgba(179,38,30,.05);'))}>
                  <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                    <span style={sx('display:block;font-size:16px;color:#22272E;overflow-wrap:break-word;')}>{x.q}</span>
                    <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:3px;overflow-wrap:break-word;')}>{ok ? '→ ' + x.got : '→ ' + x.got + ', expected ' + x.expected}</span>
                  </span>
                  <span
                    style={sx(
                      'display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;flex:0 0 22px;border-radius:6px;font-size:16px;font-weight:700;color:' +
                        (ok ? '#1F6B52;background:rgba(47,138,111,.13);' : '#B3261E;background:rgba(179,38,30,.13);'),
                    )}
                  >
                    {ok ? '✓' : '✕'}
                  </span>
                </div>
              );
            })}
            <div style={sx('font-size:14px;color:#54606C;padding:11px 14px;line-height:1.55;')}>
              Every case is a labelled question from the routing golden set. A miss is a wrong system, not a wrong answer: the gate still governed the reply.
            </div>
          </div>
        </div>

        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 15px;margin-top:13px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:9px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Notes</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>{'gate: leak ≤ ' + pct(e.thresholds.maxLeakRate, 0) + ', routing ≥ ' + pct(e.thresholds.minRoutingAcc, 0)}</span>
          </div>
          {notes.map((n) => (
            <div key={n} style={sx('display:flex;gap:9px;padding:1px 0;')}>
              <span style={sx('width:4px;height:4px;border-radius:50%;background:#2F5C97;margin-top:8px;flex:0 0 4px;')} />
              <span style={sx('font-size:16px;color:#3C454F;line-height:1.55;')}>{n}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
